import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useUpdateProfileMutation, useGetMeQuery } from '@/features/admin/api/adminApi';
import { formatDate } from '../../../../../shared/utils/date';

export function useProfile() {
  // Fetch reactive data using RTK Query
  const { data: meData, refetch: refetchMe } = useGetMeQuery(undefined, {
    refetchOnMountOrArgChange: true
  });

  const user: any = meData?.data || {};

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'edit' | 'security'>('overview');

  // Form states
  const [name, setName] = useState(user.name || '');
  const [orgName, setOrgName] = useState(user.orgName || '');

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordErrors, setShowPasswordErrors] = useState(false);

  // Mutation
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  // Permissions check
  const isOrgAdmin = user.orgRole === 'owner' || user.orgRole === 'admin';

  // Validation checks as user types
  const checks = {
    length: password.length >= 6,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[@$!%*?&]/.test(password),
  };

  // Sync initial user details when cache updates
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setOrgName(user.orgName || '');
    }
  }, [meData]);

  const handleUpdateInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Name cannot be empty',
        icon: 'error',
        confirmButtonColor: '#38bdf8',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)'
      });
      return;
    }

    const payload: { name?: string; orgName?: string } = { name };
    if (isOrgAdmin) {
      if (!orgName.trim()) {
        Swal.fire({
          title: 'Error',
          text: 'Organization name cannot be empty',
          icon: 'error',
          confirmButtonColor: '#38bdf8',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)'
        });
        return;
      }
      payload.orgName = orgName;
    }

    try {
      const res = await updateProfile(payload).unwrap();
      if (res.success) {
        if (res.data) {
          localStorage.setItem('admin_user', JSON.stringify(res.data));
        }
        await refetchMe();
        Swal.fire({
          title: 'Success',
          text: 'Profile details updated successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)'
        });
      }
    } catch (err: any) {
      Swal.fire({
        title: 'Update Failed',
        text: err?.data?.message || err?.data?.detail || 'Failed to update profile details',
        icon: 'error',
        confirmButtonColor: '#38bdf8',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)'
      });
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowPasswordErrors(true);

    if (!currentPassword || !password || !confirmPassword) {
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;
    if (!passwordRegex.test(password)) {
      return;
    }

    if (password === currentPassword) {
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    try {
      const res = await updateProfile({ newPassword: password, currentPassword }).unwrap();
      if (res.success) {
        setCurrentPassword('');
        setPassword('');
        setConfirmPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setShowPasswordErrors(false);
        Swal.fire({
          title: 'Success',
          text: 'Password updated successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)'
        });
      }
    } catch (err: any) {
      Swal.fire({
        title: 'Update Failed',
        text: err?.data?.message || err?.data?.detail || 'Failed to update password',
        icon: 'error',
        confirmButtonColor: '#38bdf8',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)'
      });
    }
  };

  const joinedDate = user.createdAt
    ? formatDate(user.createdAt)
    : 'N/A';

  return {
    user,
    activeTab,
    setActiveTab,
    name,
    setName,
    orgName,
    setOrgName,
    currentPassword,
    setCurrentPassword,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showCurrentPassword,
    setShowCurrentPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    showPasswordErrors,
    isUpdating,
    isOrgAdmin,
    checks,
    handleUpdateInfo,
    handleUpdatePassword,
    joinedDate
  };
}
